'use client';

/**
 * Per-user default chat model. Server-driven catalog, saved on change.
 *
 * DeepSeek models are grouped separately and carry an explicit warning: they
 * run on DeepSeek's external hosted API, so conversation content leaves our AWS
 * environment (see MERIDIAN_PROVIDER_SWITCHING).
 */

import React from 'react';
import type { useModelPreference } from '../../hooks/useModelPreference';

export type ModelPreference = ReturnType<typeof useModelPreference>;

const SELECT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

export function ChatModelSection({
  modelPref,
  saved,
  onChange,
}: {
  modelPref: ModelPreference;
  saved: boolean;
  onChange: (value: string) => void;
}) {
  const awsModels = modelPref.models.filter((m) => m.provider === 'aws');
  const deepseekModels = modelPref.models.filter((m) => m.provider === 'deepseek');
  const selectedIsExternal = modelPref.selectedModel?.provider === 'deepseek';

  return (
    <section>
      <h3 className="text-sm font-semibold text-foreground mb-2">Chat model</h3>
      <p className="text-xs text-muted-foreground mb-3">
        The model Meridian uses for your chats. Saved as your personal default and applied to
        every conversation until you change it.
      </p>
      {modelPref.isLoading && modelPref.models.length === 0 ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : modelPref.models.length === 0 ? (
        <p className="text-sm text-muted-foreground">Model list unavailable right now.</p>
      ) : (
        <>
          <select
            value={modelPref.selectedModelId ?? ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={modelPref.isSaving}
            className={SELECT_CLASS}
            aria-label="Default chat model"
          >
            <option value="">
              {modelPref.systemDefault
                ? `System default — ${modelPref.systemDefault.label}`
                : 'System default'}
            </option>
            {awsModels.length > 0 && (
              <optgroup label="Claude (AWS)">
                {awsModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label} — {m.description}
                  </option>
                ))}
              </optgroup>
            )}
            {deepseekModels.length > 0 && (
              <optgroup label="DeepSeek (external)">
                {deepseekModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label} — {m.description}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {selectedIsExternal && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              DeepSeek models run on DeepSeek&apos;s external hosted API — conversation content
              leaves our AWS environment, and Hub tools (CRM, email, tasks) are unavailable.
              Chat only.
            </p>
          )}
          {modelPref.error && <p className="text-sm text-destructive mt-2">{modelPref.error}</p>}
          {saved && <p className="text-sm text-green-600 dark:text-green-400 mt-2">Saved.</p>}
        </>
      )}
    </section>
  );
}
